import { useState } from 'react'
import { useRecoilValue } from 'recoil'
import { Flex } from '@/components/priority/Flex/Flex'
import { Text } from '@/components/priority/Text/Text'
import { Button } from '@/components/priority/Button/Button'
import { SupportButton } from '@/components/global/Support/SupportButton'
import { PdfViewerProps } from '@/components/pdfRender/PdfViewer'
import { ShareProofModal } from '@/components/editor/ShareProofModal/ShareProofModal'
import { SendProofModal } from '@/components/editor/EditorHeader/SendProofModal'
import { orderAtom, currentProductIdAtom } from '@/atoms/printPackageAtoms'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { printScreenshots } from '@messenger/utils'
import {
  STATUS_KEY,
  PDF_STATUS_KEY,
  M3,
  DESIGNER_SPACE_LABELS,
  EDIT_TOKEN_ROLE,
  EDIT_TOKEN_STORAGE_KEYS,
} from '@messenger/consts'
import type { ApplicationType } from '@messenger/types'

export type PdfViewerHeaderProps = Pick<
  PdfViewerProps,
  | 'canCustomize'
  | 'customizeUrl'
  | 'handleClosePdfViewer'
  | 'isProof'
  | 'setProductStatus'
  | 'pdfGenerationStatus'
  | 'proofGalleryDetails'
> & {
  pdfUrl: string
  applicationType?: ApplicationType
}

export const PdfViewerHeader = ({
  canCustomize,
  customizeUrl,
  handleClosePdfViewer,
  isProof = false,
  pdfUrl,
  pdfGenerationStatus,
  setProductStatus,
  proofGalleryDetails,
  applicationType,
}: PdfViewerHeaderProps) => {
  const { t } = useTranslation()
  const history = useHistory()
  const order = useRecoilValue(orderAtom) as { id?: string } | null
  const currentProductId = useRecoilValue(currentProductIdAtom) as unknown as
    | string
    | undefined
  const [showShareProofModal, setShowShareProofModal] = useState(false)
  const sendProofShow = useState(false)
  const [, setShowSendProofModal] = sendProofShow
  const [proofSentToClient, setProofSentToClient] = useState(false)

  // Designer token sessions resend proofs straight from the proof view —
  // no need to enter the editor first. Their view hides the staff-facing
  // support/share/print actions; the send-proof modal (note + optional
  // add-on suggestions) is the only outward action. The grant only applies
  // to the product the token was scoped to.
  const editTokenRole = sessionStorage.getItem(EDIT_TOKEN_STORAGE_KEYS.role)
  const designerToken = sessionStorage.getItem(EDIT_TOKEN_STORAGE_KEYS.token)
  const designerScope = sessionStorage.getItem(
    EDIT_TOKEN_STORAGE_KEYS.scopeOrderProductId,
  )
  const isDesignerSession =
    editTokenRole === EDIT_TOKEN_ROLE.DESIGNER &&
    Boolean(designerToken) &&
    Boolean(designerScope) &&
    designerScope === currentProductId

  const galleryToken = proofGalleryDetails?.galleryToken
  const galleryPdfId = proofGalleryDetails?.pdfId
  const screenshotUrls = proofGalleryDetails?.screenshotUrls ?? []
  const hasScreenshots = screenshotUrls.length > 0
  const isPdfGenerationComplete =
    pdfGenerationStatus === PDF_STATUS_KEY.COMPLETE
  // Banner proofs render as a PDF with no screenshot gallery. Share still
  // applies — it opens the PDF itself instead of a tokened gallery page.
  const isPdfProof = Boolean(isProof) && Boolean(pdfUrl) && !hasScreenshots
  // Show the Share button for screenshot proofs (once the gallery pdfId exists)
  // and for banner PDF proofs. Disabled until the shareable artifact is ready.
  const shouldShowProofGalleryButton =
    (Boolean(isProof) && Boolean(galleryPdfId)) || isPdfProof
  // Proof is viewable when screenshots are ready (new flow) or a PDF exists (banner/legacy)
  const hasViewableProof = hasScreenshots || Boolean(pdfUrl)
  const disableCustomize = Boolean(!hasViewableProof || !canCustomize)
  // Screenshot gallery is shareable only once the tokened gallery is fully ready.
  const canOpenProofGallery =
    Boolean(isProof) &&
    Boolean(galleryPdfId) &&
    Boolean(galleryToken) &&
    hasScreenshots &&
    isPdfGenerationComplete

  // Share is enabled once a shareable artifact exists: a tokened screenshot
  // gallery, or the PDF itself for banner proofs. The modal handles product
  // selection + emailing the tokenized share link.
  const canShare = canOpenProofGallery || isPdfProof

  const handleCustomizeClick = async () => {
    if (customizeUrl) {
      await setProductStatus(STATUS_KEY.EDITING)
      history.push(customizeUrl)
    }
  }

  const handleShareClick = () => {
    if (!canShare) {
      return
    }
    setShowShareProofModal(true)
  }

  const handlePrint = () => {
    if (!hasScreenshots) return
    printScreenshots(screenshotUrls)
  }

  return (
    <Flex
      align="center"
      justify="space-between"
      width="100%"
      padding="24px"
      margin="0 20px 0 0"
    >
      <Text size="24px" weight="bold" color="almostBlack">
        {isProof ? t('pdf.proofPreview') : t('pdf.productionProof')}
      </Text>
      <Flex gap="12px">
        {!isDesignerSession && <SupportButton />}
        {isDesignerSession && (
          // Sends the proof on screen — the exact rendered record — so no
          // duplicate render is enqueued; disabled until the artifact is
          // ready, and flips to a sent state as feedback.
          <Button
            onClick={() => setShowSendProofModal(true)}
            disabled={!hasViewableProof || proofSentToClient}
            primary
            aria-label={DESIGNER_SPACE_LABELS.sendProof.button}
            data-testid="proof-preview-send-proof-button"
          >
            {proofSentToClient
              ? DESIGNER_SPACE_LABELS.sendProof.sentBadge
              : DESIGNER_SPACE_LABELS.sendProof.button}
          </Button>
        )}
        {!isDesignerSession && shouldShowProofGalleryButton && (
          <Button
            onClick={handleShareClick}
            disabled={!canShare}
            primary
            aria-label={t('proofShare.shareButtonAriaLabel')}
            data-testid="proof-preview-share-button"
          >
            {t('proofShare.shareButton')}
          </Button>
        )}
        {!isDesignerSession && hasScreenshots && (
          <Button
            onClick={handlePrint}
            primary
            aria-label={t('proofGallery.printAriaLabel')}
          >
            {t('proofGallery.printLabel')}
          </Button>
        )}
        <Button
          onClick={handleCustomizeClick}
          disabled={disableCustomize}
          aria-label={
            applicationType === M3
              ? t('customizeProductHeader.customizeAriaLabelM3')
              : t('customizeProductHeader.customizeAriaLabel')
          }
        >
          {applicationType === M3
            ? t('customizeProductHeader.customizeM3')
            : t('customizeProductHeader.customize')}
        </Button>
        <Button
          data-testid="pdf-viewer-close"
          onClick={handleClosePdfViewer}
          aria-label={t('pdf.closeButtonAriaLabel')}
        >
          {t('pdf.closeButton')}
        </Button>
      </Flex>

      <ShareProofModal
        show={showShareProofModal}
        orderId={order?.id}
        preselectedOrderProductId={currentProductId}
        onClose={() => setShowShareProofModal(false)}
      />

      {isDesignerSession && (
        <SendProofModal
          show={sendProofShow}
          token={designerToken}
          reusePdfRecordId={galleryPdfId ?? undefined}
          onSent={() => setProofSentToClient(true)}
        />
      )}
    </Flex>
  )
}
